import type { DataSeries } from '@/generated/model';
import type { TimeRangeLane, TimeSeries, TimeSeriesChartHighlight } from '@/shared/charts';

import { LINE_GAP_THRESHOLD_MS, LINE_SAMPLING } from '../../config/chartConfig';
import type { DiagramSeriesOption } from '../../types/DiagramSeries.types';
import type { TimeSeriesChartContent } from '../../types/TimeSeriesChartModel.types';
import { getDataSeriesPoints, getDataSeriesRanges } from '../data-series/dataSeriesMappers';
import type { TimeWindowMicros } from '../time-window/timeWindow';

type MapDataSeriesToChartContentParams = {
  metadata: DiagramSeriesOption[];
  selectedSeriesIds: string[];
  dataSeries: DataSeries[];
  getSeriesColor: (seriesId: string) => string;
  timeWindow: TimeWindowMicros;
};

function mapNumericSeries(
  option: DiagramSeriesOption,
  dataSeries: DataSeries | undefined,
  color: string,
  timeWindow: TimeWindowMicros
): TimeSeries {
  return {
    id: option.id,
    label: option.label,
    unit: option.unit,
    color,
    sampling: LINE_SAMPLING,
    gapThresholdMs: LINE_GAP_THRESHOLD_MS,
    points: dataSeries ? getDataSeriesPoints(dataSeries, timeWindow) : [],
  };
}

function mapStateLane(
  option: DiagramSeriesOption,
  dataSeries: DataSeries | undefined,
  color: string,
  timeWindow: TimeWindowMicros
): TimeRangeLane {
  return {
    id: option.id,
    label: option.label,
    color,
    ranges: dataSeries ? getDataSeriesRanges(dataSeries, timeWindow) : [],
  };
}

/**
 * Lines come from the selected numeric series and range-track lanes from the selected state series,
 * so a checkbox shows or hides a state exactly like it does a measurement channel. This part does
 * not depend on the highlight, so a highlight toggle can reuse it.
 */
export function mapDataSeriesToChartContent({
  metadata,
  selectedSeriesIds,
  dataSeries,
  getSeriesColor,
  timeWindow,
}: MapDataSeriesToChartContentParams): TimeSeriesChartContent {
  const selectedIds = new Set(selectedSeriesIds);
  const seriesById = new Map(dataSeries.map((series) => [series.id, series]));

  const series = metadata
    .filter((option) => option.valueType === 'number' && selectedIds.has(option.id))
    .map((option) => mapNumericSeries(option, seriesById.get(option.id), getSeriesColor(option.id), timeWindow));

  const lanes = metadata
    .filter((option) => option.valueType === 'boolean' && selectedIds.has(option.id))
    .map((option) => mapStateLane(option, seriesById.get(option.id), getSeriesColor(option.id), timeWindow));

  return {
    series,
    ...(lanes.length > 0 ? { rangeTrack: { lanes } } : {}),
  };
}

/** Highlighting is an explicit selection on top of both the lines and the lanes. */
export function getChartHighlight(
  { series, rangeTrack }: TimeSeriesChartContent,
  highlightedSeriesIds: string[]
): TimeSeriesChartHighlight {
  const highlightedIds = new Set(highlightedSeriesIds);
  const lineSeriesIds = series.map(({ id }) => id);
  const laneIds = rangeTrack?.lanes.map((lane) => lane.id) ?? [];

  return {
    seriesIds: lineSeriesIds.filter((seriesId) => highlightedIds.has(seriesId)),
    laneIds: laneIds.filter((laneId) => highlightedIds.has(laneId)),
  };
}

/**
 * Which line series' shape backs the navigation slider's preview: the most recently highlighted
 * one (a deliberate "look at this" action), or failing that the most recently selected one — both
 * checkbox and chip interactions append to the end of their respective id lists, so "most recent"
 * is simply the last id that still resolves to a line series (a highlighted or selected state lane
 * lives in the range track, not among these, so it is skipped here rather than picked by mistake).
 */
export function getPreviewSeriesId(
  series: TimeSeries[],
  highlightedSeriesIds: string[],
  selectedSeriesIds: string[]
): string | undefined {
  const lineSeriesIds = new Set(series.map(({ id }) => id));
  const findLastLineSeriesId = (seriesIds: string[]) =>
    [...seriesIds].reverse().find((seriesId) => lineSeriesIds.has(seriesId));

  return findLastLineSeriesId(highlightedSeriesIds) ?? findLastLineSeriesId(selectedSeriesIds);
}
